import { useCallback, useState } from "react";

const API_BASE = "https://scraper.synorb.com/api";

const TABS = [
  { id: "html",  label: "HTML Scraper",  icon: "⬡", endpoint: "/html/generate",         color: "#334155" },
  { id: "get",   label: "GET API",       icon: "↓", endpoint: "/open_api_get/generate",  color: "#0369a1" },
  { id: "post",  label: "POST API",      icon: "↑", endpoint: "/open_api_post/generate", color: "#0f766e" },
];

const defaultDbc = {
  companyName: "",
  entityId: "",
  entityDetails: [
    { entity_value: "", entity_type: "source_data" },
    { entity_value: "S&P 500", entity_type: "index" },
  ],
  outputFilePath: "",
  entityTypeName: "",
  dataBlockCategory: "market_data",
};

// ─── Base styles ──────────────────────────────────────────────────────────────

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "#ffffff", border: "1px solid #e2e8f0",
  borderRadius: 7, padding: "9px 12px",
  color: "#0f172a", fontSize: 13,
  fontFamily: "'IBM Plex Mono', monospace",
  outline: "none", transition: "border-color 0.15s, box-shadow 0.15s",
};

// ─── Primitives ───────────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  return (
    <label style={{
      display: "block", fontSize: 11, fontWeight: 700,
      letterSpacing: "0.08em", textTransform: "uppercase",
      color: "#334155", marginBottom: 6,
    }}>{children}</label>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {children}
    </div>
  );
}

function Inp({ value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{
        ...inputStyle,
        borderColor: focus ? "#334155" : "#e2e8f0",
        boxShadow: focus ? "0 0 0 3px rgba(51,65,85,0.08)" : "none",
      }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />
  );
}

function Txta({ value, onChange, placeholder, rows = 5 }) {
  const [focus, setFocus] = useState(false);
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{
        ...inputStyle, resize: "vertical",
        borderColor: focus ? "#334155" : "#e2e8f0",
        boxShadow: focus ? "0 0 0 3px rgba(51,65,85,0.08)" : "none",
      }}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />
  );
}

function Sel({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ ...inputStyle, cursor: "pointer", background: "#fff" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─── Tag Input ────────────────────────────────────────────────────────────────

function TagInput({ label, values, onChange }) {
  const [inp, setInp] = useState("");
  const add = () => {
    const v = inp.trim();
    if (v && !values.includes(v)) { onChange([...values, v]); setInp(""); }
  };
  return (
    <Row label={label}>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <input value={inp} onChange={e => setInp(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="field name → Enter"
          style={{ ...inputStyle, flex: 1 }} />
        <button onClick={add} style={{
          background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 7,
          color: "#334155", padding: "9px 14px", cursor: "pointer",
          fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
          transition: "background 0.15s",
        }}
          onMouseEnter={e => e.target.style.background = "#e2e8f0"}
          onMouseLeave={e => e.target.style.background = "#f1f5f9"}
        >+ Add</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {values.map((v, i) => (
          <span key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#f1f5f9", border: "1px solid #e2e8f0",
            borderRadius: 20, padding: "4px 10px 4px 12px",
            fontSize: 12, color: "#334155", fontWeight: 500,
          }}>
            {v}
            <span onClick={() => onChange(values.filter((_, j) => j !== i))}
              style={{ cursor: "pointer", color: "#94a3b8", fontWeight: 700, fontSize: 14, lineHeight: 1 }}>×</span>
          </span>
        ))}
        {values.length === 0 && (
          <span style={{ fontSize: 11, color: "#cbd5e1", fontStyle: "italic" }}>None added</span>
        )}
      </div>
    </Row>
  );
}

// ─── Entity Details ───────────────────────────────────────────────────────────

function EntityDetails({ details, onChange }) {
  const update = (i, k, v) => onChange(details.map((d, j) => j === i ? { ...d, [k]: v } : d));
  return (
    <Row label="Entity Details">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {details.map((d, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 34px", gap: 6 }}>
            <input value={d.entity_value} onChange={e => update(i, "entity_value", e.target.value)}
              placeholder="entity_value" style={inputStyle} />
            <input value={d.entity_type} onChange={e => update(i, "entity_type", e.target.value)}
              placeholder="entity_type" style={inputStyle} />
            <button onClick={() => onChange(details.filter((_, j) => j !== i))}
              style={{
                background: "#fff5f5", border: "1px solid #fecaca",
                borderRadius: 7, color: "#ef4444", cursor: "pointer", fontSize: 14,
                transition: "background 0.15s",
              }}>×</button>
          </div>
        ))}
        <button onClick={() => onChange([...details, { entity_value: "", entity_type: "" }])}
          style={{
            background: "transparent", border: "1px dashed #cbd5e1", borderRadius: 7,
            color: "#94a3b8", padding: "8px", cursor: "pointer", fontSize: 12,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.target.style.color = "#334155"; e.target.style.borderColor = "#94a3b8"; }}
          onMouseLeave={e => { e.target.style.color = "#94a3b8"; e.target.style.borderColor = "#cbd5e1"; }}
        >+ Add Row</button>
      </div>
    </Row>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

function Panel({ title, accent = "#334155", children, badge }) {
  return (
    <div style={{
      background: "#ffffff", border: "1px solid #e2e8f0",
      borderRadius: 12, marginBottom: 16, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "11px 16px", borderBottom: "1px solid #f1f5f9",
        background: "#f8fafc",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 3, height: 14, borderRadius: 2, background: accent }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", letterSpacing: "0.03em" }}>{title}</span>
        </div>
        {badge && (
          <span style={{
            fontSize: 10, background: "#e2e8f0", color: "#64748b",
            borderRadius: 4, padding: "2px 8px", fontWeight: 600,
          }}>{badge}</span>
        )}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

// ─── Status Banner ────────────────────────────────────────────────────────────

function StatusBanner({ status, message }) {
  if (!status) return null;
  const map = {
    loading: { bg: "#eff6ff", border: "#bfdbfe", dot: "#3b82f6", text: "#1d4ed8", pulse: true },
    success: { bg: "#f0fdf4", border: "#bbf7d0", dot: "#22c55e", text: "#15803d", pulse: false },
    error:   { bg: "#fff5f5", border: "#fecaca", dot: "#ef4444", text: "#dc2626", pulse: false },
  };
  const c = map[status];
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 10, padding: "12px 16px", marginBottom: 20,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: "50%", background: c.dot,
        flexShrink: 0, marginTop: 3,
        animation: c.pulse ? "pulse 1.2s ease-in-out infinite" : "none",
      }} />
      <span style={{ fontSize: 13, color: c.text, fontWeight: 600, lineHeight: 1.5 }}>{message}</span>
    </div>
  );
}

// ─── Code Viewer ──────────────────────────────────────────────────────────────

function CodeViewer({ code, onClear }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const lines = code.split("\n");
  return (
    <div style={{
      background: "#1e293b", border: "1px solid #334155",
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
    }}>
      {/* toolbar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", background: "#0f172a", borderBottom: "1px solid #334155",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", gap: 5 }}>
            {["#ef4444","#f59e0b","#22c55e"].map((c,i)=>
              <div key={i} style={{ width:11, height:11, borderRadius:"50%", background:c }} />
            )}
          </div>
          <span style={{ fontSize: 12, color: "#64748b", marginLeft: 4, fontFamily: "'IBM Plex Mono', monospace" }}>scraper.py</span>
          <span style={{
            fontSize: 11, background: "#1e293b", color: "#64748b",
            borderRadius: 4, padding: "2px 7px", border: "1px solid #334155",
          }}>{lines.length} lines</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={copy} style={{
            background: copied ? "#064e3b" : "#1e293b",
            border: `1px solid ${copied ? "#34d399" : "#334155"}`,
            borderRadius: 6, color: copied ? "#34d399" : "#94a3b8",
            padding: "5px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600,
            transition: "all 0.2s",
          }}>{copied ? "✓ Copied!" : "Copy"}</button>
          <button onClick={onClear} style={{
            background: "#1e293b", border: "1px solid #334155", borderRadius: 6,
            color: "#64748b", padding: "5px 14px", cursor: "pointer", fontSize: 12,
          }}>Clear</button>
        </div>
      </div>
      {/* code */}
      <div style={{ display: "flex", maxHeight: 520, overflow: "auto" }}>
        <div style={{
          padding: "16px 12px", background: "#0f172a",
          borderRight: "1px solid #334155", textAlign: "right",
          userSelect: "none", minWidth: 44, flexShrink: 0,
        }}>
          {lines.map((_, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: "1.8", color: "#475569", fontFamily: "'IBM Plex Mono', monospace" }}>
              {i + 1}
            </div>
          ))}
        </div>
        <pre style={{
          margin: 0, padding: "16px 20px", flex: 1,
          fontSize: 12.5, lineHeight: 1.8, color: "#e2e8f0",
          fontFamily: "'IBM Plex Mono', monospace", overflow: "auto",
        }}>{code}</pre>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [tab, setTab] = useState("html");
  const activeTab = TABS.find(t => t.id === tab);

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [detailUrl,  setDetailUrl]  = useState("");
  const [mainMand,   setMainMand]   = useState(["title", "url", "published_date"]);
  const [mainOpt,    setMainOpt]    = useState([]);
  const [detMand,    setDetMand]    = useState(["content"]);
  const [detOpt,     setDetOpt]     = useState([]);
  const [headers,    setHeaders]    = useState("");
  const [openApiRes, setOpenApiRes] = useState("");
  const [payload,    setPayload]    = useState("");
  const [dbc,        setDbc]        = useState(defaultDbc);
  const upd = (k, v) => setDbc(p => ({ ...p, [k]: v }));

  const [status,    setStatus]    = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [result,    setResult]    = useState(null);

  const buildBody = useCallback(() => {
    let parsedHeaders = null;
    if (headers.trim()) {
      try { parsedHeaders = JSON.parse(headers); }
      catch { throw new Error("Headers field contains invalid JSON"); }
    }
    const dataBlockConfig = {
      companyName: dbc.companyName,
      entityId: dbc.entityId ? parseInt(dbc.entityId) : null,
      entityDetails: dbc.entityDetails.filter(d => d.entity_value),
      outputFilePath: dbc.outputFilePath || null,
      entityTypeCreationPayload: {
        data_block_provider_id: String(dbc.entityId || ""),
        name: dbc.entityTypeName,
        data_block_category: dbc.dataBlockCategory,
      },
    };
    const base = {
      websiteUrl, detailPageUrl: detailUrl,
      mainPageMandatoryFields: mainMand, mainPageOptionalFields: mainOpt,
      detailPageMandatoryFields: detMand, detailPageOptionalFields: detOpt,
      headers: parsedHeaders, dataBlockConfig,
    };
    if (tab === "html") return { body: base, endpoint: activeTab.endpoint };
    let openApiResValue;
    try { openApiResValue = JSON.parse(openApiRes); }
    catch { openApiResValue = openApiRes; }
    const apiBody = { ...base, openApiRes: openApiResValue };
    if (tab === "post") {
      let p = null;
      if (payload.trim()) {
        try { p = JSON.parse(payload); }
        catch { throw new Error("Payload field contains invalid JSON"); }
      }
      apiBody.payload = p;
    }
    return { body: apiBody, endpoint: activeTab.endpoint };
  }, [tab, activeTab, websiteUrl, detailUrl, mainMand, mainOpt, detMand, detOpt, headers, openApiRes, payload, dbc]);

  const generate = async () => {
    if (!websiteUrl || !detailUrl) {
      setStatus("error"); setStatusMsg("Website URL and Detail Page URL are required."); return;
    }
    setStatus("loading"); setStatusMsg("Calling LLM — this may take 20–60 seconds…"); setResult(null);
    try {
      const { body, endpoint } = buildBody();
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(`Server ${res.status}: ${text}`);
      if (text.startsWith("HTML does not") || text.startsWith("JSON does not")) {
        setStatus("error"); setStatusMsg(text);
      } else {
        setStatus("success");
        setStatusMsg(`Script generated successfully${dbc.outputFilePath ? " · Saved to " + dbc.outputFilePath : ""}`);
        setResult(text);
      }
    } catch (e) { setStatus("error"); setStatusMsg(e.message); }
  };

  const isApi  = tab !== "html";
  const isPost = tab === "post";

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", color: "#0f172a", fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>

      {/* ── Sidebar ── */}
      <div style={{
        position: "fixed", top: 0, left: 0, bottom: 0, width: 224,
        background: "#ffffff", borderRight: "1px solid #e2e8f0",
        display: "flex", flexDirection: "column", zIndex: 10,
        boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
      }}>
        {/* Logo */}
        <div style={{ padding: "20px 18px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 17, flexShrink: 0, color: "#fff",
              boxShadow: "0 2px 8px rgba(30,41,59,0.3)",
            }}>⚡</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>Scraper Engine</div>
              <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>Synorb AI</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ padding: "14px 10px", flex: 1 }}>
          <div style={{ fontSize: 10, color: "#cbd5e1", letterSpacing: "0.1em", textTransform: "uppercase", padding: "0 8px 10px" }}>
            Scraper Type
          </div>
          {TABS.map(t => (
            <button key={t.id}
              onClick={() => { setTab(t.id); setResult(null); setStatus(null); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                background: tab === t.id ? "#f1f5f9" : "transparent",
                border: "none",
                borderLeft: tab === t.id ? `3px solid ${t.color}` : "3px solid transparent",
                borderRadius: tab === t.id ? "0 8px 8px 0" : 8,
                padding: "10px 12px", cursor: "pointer",
                color: tab === t.id ? "#0f172a" : "#64748b",
                fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
                textAlign: "left", marginBottom: 2, transition: "all 0.15s",
              }}
              onMouseEnter={e => { if (tab !== t.id) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={e => { if (tab !== t.id) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{
                width: 24, height: 24, borderRadius: 6,
                background: tab === t.id ? t.color : "#f1f5f9",
                color: tab === t.id ? "#fff" : "#94a3b8",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, flexShrink: 0, transition: "all 0.15s",
              }}>{t.icon}</span>
              {t.label}
            </button>
          ))}

          {/* Endpoint info */}
          <div style={{ marginTop: 20, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Endpoint</div>
            <div style={{ fontSize: 11, color: activeTab.color, fontFamily: "'IBM Plex Mono', monospace", wordBreak: "break-all", fontWeight: 600 }}>
              POST /api{activeTab.endpoint}
            </div>
          </div>

          {/* Token map */}
          <div style={{ marginTop: 10, padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Token Map</div>
            {[
              ["ENTITY_ID", dbc.entityId || "—"],
              ["ORG", dbc.companyName || "—"],
              ["TYPE_ID", "← API"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#475569", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>{k}</span>
                <span style={{ fontSize: 10, color: "#94a3b8", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid #f1f5f9" }}>
          <div style={{ fontSize: 11, color: "#cbd5e1" }}>v1.0.0 · LLM Scraper Engine</div>
        </div>
      </div>

      {/* ── Main ── */}
      <div style={{ marginLeft: 224, minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {/* Top bar */}
        <div style={{
          height: 58, borderBottom: "1px solid #e2e8f0", background: "#ffffff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 28px", position: "sticky", top: 0, zIndex: 5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{activeTab.label}</span>
            <span style={{
              fontSize: 11, color: activeTab.color, fontWeight: 700,
              background: activeTab.color + "12",
              border: `1px solid ${activeTab.color}30`,
              borderRadius: 5, padding: "2px 9px",
            }}>
              {tab === "html" ? "HTML Parsing" : tab === "get" ? "GET Request" : "POST Request"}
            </span>
          </div>
          <button onClick={generate} disabled={status === "loading"} style={{
            display: "flex", alignItems: "center", gap: 8,
            background: status === "loading"
              ? "#f1f5f9"
              : "linear-gradient(135deg, #1e293b, #334155)",
            border: "none", borderRadius: 9,
            color: status === "loading" ? "#94a3b8" : "#ffffff",
            padding: "9px 22px", cursor: status === "loading" ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: 700, transition: "all 0.2s",
            boxShadow: status === "loading" ? "none" : "0 2px 12px rgba(30,41,59,0.25)",
          }}>
            {status === "loading"
              ? <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid #cbd5e1", borderTopColor: "#94a3b8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Generating…</>
              : <>⚡ Generate Script</>
            }
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 28, flex: 1 }}>
          <StatusBanner status={status} message={statusMsg} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

            {/* LEFT */}
            <div>
              <Panel title="URLs" accent={activeTab.color}>
                <Row label="Website / API URL *">
                  <Inp value={websiteUrl} onChange={setWebsiteUrl}
                    placeholder={tab === "html" ? "https://example.com/blog/" : "https://api.example.com/posts"} />
                </Row>
                <Row label="Detail Page URL *">
                  <Inp value={detailUrl} onChange={setDetailUrl}
                    placeholder="https://example.com/blog/sample-post" />
                </Row>
              </Panel>

              <Panel title="Page Fields" accent="#475569">
                <TagInput label="Main Page — Mandatory" values={mainMand} onChange={setMainMand} />
                <TagInput label="Main Page — Optional"  values={mainOpt}  onChange={setMainOpt} />
                <TagInput label="Detail Page — Mandatory" values={detMand} onChange={setDetMand} />
                <TagInput label="Detail Page — Optional"  values={detOpt}  onChange={setDetOpt} />
              </Panel>

              <Panel title="Request Headers" badge="Optional" accent="#94a3b8">
                <Row label="JSON Object">
                  <Txta value={headers} onChange={setHeaders}
                    placeholder={'{\n  "Authorization": "Bearer token",\n  "Cookie": "session=xyz"\n}'} rows={4} />
                </Row>
              </Panel>

              {isApi && (
                <Panel title={isPost ? "API Response + POST Payload" : "API Response"} accent={activeTab.color}>
                  <Row label="First Page Response (JSON or HTML string)">
                    <Txta value={openApiRes} onChange={setOpenApiRes}
                      placeholder={isPost
                        ? '"<!-- HTML response -->" or {"results": [...]}'
                        : '{"articles": [...], "totalPages": 5}'}
                      rows={7} />
                  </Row>
                  {isPost && (
                    <Row label="POST Payload">
                      <Txta value={payload} onChange={setPayload}
                        placeholder={'{\n  "p": 1,\n  "f": "",\n  "region": "North America"\n}'} rows={4} />
                    </Row>
                  )}
                </Panel>
              )}
            </div>

            {/* RIGHT */}
            <div>
              <Panel title="DataBlock Configuration" accent="#0369a1">
                <Row label="Company Name *">
                  <Inp value={dbc.companyName} onChange={v => upd("companyName", v)} placeholder="CH Robinson" />
                </Row>
                <Row label="Provider / Entity ID *">
                  <Inp value={dbc.entityId} onChange={v => upd("entityId", v)} placeholder="1350" />
                </Row>
                <EntityDetails details={dbc.entityDetails} onChange={v => upd("entityDetails", v)} />
                <Row label="Output File Path">
                  <Inp value={dbc.outputFilePath} onChange={v => upd("outputFilePath", v)}
                    placeholder="D:\\Python\\scrapers\\company.py" />
                </Row>
              </Panel>

              <Panel title="Entity Type API Payload" accent="#0f766e">
                <Row label="Name *">
                  <Inp value={dbc.entityTypeName} onChange={v => upd("entityTypeName", v)} placeholder="CH Robinson Blog" />
                </Row>
                <Row label="Data Block Category">
                  <Sel value={dbc.dataBlockCategory} onChange={v => upd("dataBlockCategory", v)}
                    options={[
                      { value: "market_data", label: "market_data" },
                      { value: "news", label: "news" },
                      { value: "financial", label: "financial" },
                      { value: "research", label: "research" },
                    ]} />
                </Row>
                {/* Live preview */}
                <div style={{ background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <div style={{ padding: "7px 12px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 6, background: "#f1f5f9" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0f766e" }} />
                    <span style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
                      Payload Preview
                    </span>
                  </div>
                  <pre style={{ margin: 0, padding: "12px 14px", fontSize: 11.5, color: "#475569", fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.7 }}>
{JSON.stringify({
  data_block_provider_id: dbc.entityId || "<entityId>",
  name: dbc.entityTypeName || "<name>",
  data_block_category: dbc.dataBlockCategory,
}, null, 2)}
                  </pre>
                </div>
              </Panel>
            </div>
          </div>

          {/* Code output — full width */}
          {result && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                <span style={{
                  fontSize: 11, color: "#64748b", letterSpacing: "0.08em",
                  textTransform: "uppercase", fontWeight: 700,
                  background: "#f1f5f9", padding: "4px 14px", borderRadius: 20,
                  border: "1px solid #e2e8f0",
                }}>Generated Output</span>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              </div>
              <CodeViewer code={result} onClear={() => { setResult(null); setStatus(null); }} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8fafc; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        input::placeholder, textarea::placeholder { color: #94a3b8; }
        select option { background: #fff; color: #0f172a; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
}